/**
 * Custom event kural motoru.
 *
 * Panelde kod yazmadan tanimlanan kurallari (Setting.webAnalytics.eventRules)
 * tarayicida dinler ve tetiklenince tracker event'i uretir.
 * Editor test modunda event'ler transport'a gitmez, panele geri gonderilir.
 */
import log2 from "../../../js/log2-lite";
import { TRIGGERS, LIMITS, RULE_SOURCE, VISIBLE_RATIO, VISIBLE_DURATION_MS } from "./schema";
import pathMatcher from "./paths";
import { resolveProps, evaluateCondition } from "./resolveProps";
import { clearCompileCache } from "./jsRunner";

const log = log2("TRACKER_RULES");

const ONCE_STORE_KEY = "_supsis_rule_once";

class RulesEngine {
    constructor() {
        this.tracker = null;
        this.siteId = null;
        this.rules = [];
        this.byTrigger = { click: [], visible: [], submit: [], pageview: [], sdk: [] };
        this.started = false;
        this.listenersBound = false;
        this.observer = null;
        this.visibleTimers = {};
        this.pageOnce = {};
        this.testHandler = null;
    }

    /**
     * @param {Object} tracker SupsisTracker
     * @param {String} siteId
     */
    attach(tracker, siteId) {
        this.tracker = tracker;
        this.siteId = siteId;
    }

    /**
     * Kural setini derler. Gecersiz kurallar sessizce atlanir (tracker asla patlamamali).
     * @param {Object} ruleset { rules: [], paths: [] }
     */
    configure(ruleset) {
        const payload = ruleset && typeof ruleset === "object" ? ruleset : {};
        const source = Array.isArray(payload.rules) ? payload.rules.slice(0, LIMITS.maxRules) : [];

        pathMatcher.configure(payload.paths);
        clearCompileCache();

        this.rules = [];
        this.byTrigger = { click: [], visible: [], submit: [], pageview: [], sdk: [] };

        for (let i = 0; i < source.length; i++) {
            const rule = this.compileRule(source[i]);
            if (!rule) continue;
            this.rules.push(rule);
            this.byTrigger[rule.when].push(rule);
        }

        log.debug("ruleset configured", { count: this.rules.length });
        return this.rules.length;
    }

    compileRule(raw) {
        if (!raw || typeof raw !== "object") return null;
        if (raw.enabled === false) return null;

        const when = String(raw.when || "");
        if (!this.byTrigger[when]) return null;
        if (!raw.name || !raw.id) return null;

        const selector = typeof raw.sel === "string" ? raw.sel.trim() : "";
        const needsSelector = when === TRIGGERS.CLICK || when === TRIGGERS.VISIBLE || when === TRIGGERS.SUBMIT;
        if (needsSelector && !selector) return null;

        return {
            id: String(raw.id),
            name: String(raw.name),
            when: when,
            sel: selector,
            fallbackSel: typeof raw.fallbackSel === "string" ? raw.fallbackSel.trim() : "",
            paths: Array.isArray(raw.paths) ? raw.paths : [],
            once: raw.once || null,
            if: typeof raw.if === "string" && raw.if ? raw.if : null,
            map: raw.map && typeof raw.map === "object" ? raw.map : {},
        };
    }

    /** Dinleyicileri baglar; kural yoksa DOM'a dokunulmaz. */
    start() {
        this.started = true;
        if (this.rules.length === 0) return;
        this.bindListeners();
        this.refreshVisibleTargets();
        this.onPageView();
    }

    bindListeners() {
        if (this.listenersBound) return;
        this.listenersBound = true;
        const self = this;

        document.addEventListener(
            "click",
            function (event) {
                try {
                    self.onClick(event);
                } catch (e) {
                    log.debug("click rule failed", e);
                }
            },
            true
        );

        document.addEventListener(
            "submit",
            function (event) {
                try {
                    self.onSubmit(event);
                } catch (e) {
                    log.debug("submit rule failed", e);
                }
            },
            true
        );

        this.observeVisibleNodes();
    }

    /* ----------------------------- tetikleyiciler ----------------------------- */

    /**
     * Her fiziksel tiklama tek bir click event'i uretir ve kural basina en fazla bir
     * eslesme aranir; bu yuzden hiz sinirlamasi uygulanmaz. Tekrari kisitlamak isteyen
     * kullanici kuralda `once` ayarini kullanir.
     */
    onClick(event) {
        const rules = this.byTrigger.click;
        if (rules.length === 0) return;

        for (let i = 0; i < rules.length; i++) {
            const rule = rules[i];
            if (!this.isInScope(rule)) continue;
            const element = this.closestMatch(event.target, rule);
            if (!element) continue;
            this.trigger(rule, element, event);
        }
    }

    onSubmit(event) {
        const rules = this.byTrigger.submit;
        if (rules.length === 0) return;

        const form = event.target;
        for (let i = 0; i < rules.length; i++) {
            const rule = rules[i];
            if (!this.isInScope(rule)) continue;
            // Secici formun kendisini ya da icindeki bir elementi hedefleyebilir
            const element = this.matchesForm(form, rule);
            if (!element) continue;
            this.trigger(rule, element, event);
        }
    }

    /** page_view sonrasi cagrilir (SPA gecisleri dahil). */
    onPageView() {
        if (!this.started) return;

        // Sayfa bazli "bir kez" isaretleri her gecişte sifirlanir
        this.pageOnce = {};
        this.refreshVisibleTargets();

        const rules = this.byTrigger.pageview;
        for (let i = 0; i < rules.length; i++) {
            const rule = rules[i];
            if (!this.isInScope(rule)) continue;
            // Secici verildiyse sayfada o element bulunmali
            const element = rule.sel ? this.querySelector(rule) : null;
            if (rule.sel && !element) continue;
            this.trigger(rule, element, null);
        }
    }

    /** SDK track komutunda ayni isimli kural var mi. */
    hasSdkRule(name) {
        const rules = this.byTrigger.sdk;
        for (let i = 0; i < rules.length; i++) {
            if (rules[i].name === name) return true;
        }
        return false;
    }

    /**
     * SDK ile gonderilen event'i kural property'leriyle zenginlestirir.
     * SDK'dan gelen property'ler kuralin uzerine yazar.
     * @returns {Promise<Object>} birlesik props
     */
    enrichSdkProps(name, props) {
        const rules = this.byTrigger.sdk;
        const matched = [];
        for (let i = 0; i < rules.length; i++) {
            if (rules[i].name === name && this.isInScope(rules[i])) matched.push(rules[i]);
        }
        if (matched.length === 0) return Promise.resolve(props || {});

        const self = this;
        const merged = {};
        const sdkEvent = { name: name, props: props || {} };

        function step(index) {
            if (index >= matched.length) return Promise.resolve(merged);
            const rule = matched[index];
            const element = rule.sel ? self.querySelector(rule) : null;

            return resolveProps(rule, self.buildContext(element, sdkEvent)).then(function (resolved) {
                Object.assign(merged, resolved);
                return step(index + 1);
            });
        }

        return step(0).then(function (ruleProps) {
            return Object.assign(ruleProps, props || {});
        });
    }

    /* ------------------------------- gorunurluk ------------------------------- */

    refreshVisibleTargets() {
        const rules = this.byTrigger.visible;
        if (rules.length === 0 || !window.IntersectionObserver) return;

        if (!this.observer) {
            const self = this;
            this.observer = new IntersectionObserver(
                function (entries) {
                    for (let i = 0; i < entries.length; i++) self.onIntersect(entries[i]);
                },
                { threshold: [VISIBLE_RATIO] }
            );
        }

        for (let i = 0; i < rules.length; i++) {
            const rule = rules[i];
            if (!this.isInScope(rule)) continue;
            const nodes = this.querySelectorAll(rule);
            for (let j = 0; j < nodes.length; j++) this.observer.observe(nodes[j]);
        }
    }

    /** Sonradan DOM'a eklenen elementler de izlenir. */
    observeVisibleNodes() {
        if (this.byTrigger.visible.length === 0) return;
        if (!window.MutationObserver || !document.body) return;

        const self = this;
        let pending = null;
        new MutationObserver(function () {
            // DOM hareketli sayfalarda her mutasyonda selector calistirmamak icin toparlanir
            if (pending) return;
            pending = setTimeout(function () {
                pending = null;
                self.refreshVisibleTargets();
            }, 500);
        }).observe(document.body, { childList: true, subtree: true });
    }

    onIntersect(entry) {
        const rules = this.byTrigger.visible;
        const self = this;

        for (let i = 0; i < rules.length; i++) {
            const rule = rules[i];
            if (!this.elementMatches(entry.target, rule)) continue;
            const timerKey = rule.id + ":" + i;

            if (!entry.isIntersecting || entry.intersectionRatio < VISIBLE_RATIO) {
                if (this.visibleTimers[timerKey]) {
                    clearTimeout(this.visibleTimers[timerKey]);
                    delete this.visibleTimers[timerKey];
                }
                continue;
            }

            if (this.visibleTimers[timerKey] || this.isOnceConsumed(rule)) continue;

            const target = entry.target;
            // Kisa temaslar sayilmaz; element bir sure gorunur kalmalidir
            this.visibleTimers[timerKey] = setTimeout(function () {
                delete self.visibleTimers[timerKey];
                if (!self.isInScope(rule)) return;
                self.trigger(rule, target, null);
                if (self.observer) self.observer.unobserve(target);
            }, VISIBLE_DURATION_MS);
        }
    }

    /* -------------------------------- yardimci -------------------------------- */

    isInScope(rule) {
        return pathMatcher.matches(rule.paths);
    }

    selectors(rule) {
        const list = [];
        if (rule.sel) list.push(rule.sel);
        if (rule.fallbackSel) list.push(rule.fallbackSel);
        return list;
    }

    /** Tiklanan elementten yukari dogru kural seciciyi arar. */
    closestMatch(target, rule) {
        if (!target || !target.closest) return null;
        const selectors = this.selectors(rule);
        for (let i = 0; i < selectors.length; i++) {
            try {
                const found = target.closest(selectors[i]);
                if (found) return found;
            } catch (e) {
                /* gecersiz secici */
            }
        }
        return null;
    }

    /** Gonderilen form kuralla eslesiyor mu (form kendisi veya icindeki element). */
    matchesForm(form, rule) {
        if (!form) return null;
        if (this.elementMatches(form, rule)) return form;
        const selectors = this.selectors(rule);
        for (let i = 0; i < selectors.length; i++) {
            try {
                const inner = form.querySelector(selectors[i]);
                if (inner) return form;
            } catch (e) {
                /* gecersiz secici */
            }
        }
        return null;
    }

    elementMatches(element, rule) {
        if (!element || !element.matches) return false;
        const selectors = this.selectors(rule);
        for (let i = 0; i < selectors.length; i++) {
            try {
                if (element.matches(selectors[i])) return true;
            } catch (e) {
                /* gecersiz secici */
            }
        }
        return false;
    }

    querySelector(rule) {
        const selectors = this.selectors(rule);
        for (let i = 0; i < selectors.length; i++) {
            try {
                const node = document.querySelector(selectors[i]);
                if (node) return node;
            } catch (e) {
                /* gecersiz secici */
            }
        }
        return null;
    }

    querySelectorAll(rule) {
        const selectors = this.selectors(rule);
        for (let i = 0; i < selectors.length; i++) {
            try {
                const nodes = document.querySelectorAll(selectors[i]);
                if (nodes.length > 0) return nodes;
            } catch (e) {
                /* gecersiz secici */
            }
        }
        return [];
    }

    /** Panelde "su an N element eslesiyor" rozeti icin. */
    countMatches(selector) {
        if (!selector) return 0;
        try {
            return document.querySelectorAll(selector).length;
        } catch (e) {
            return -1;
        }
    }

    isOnceConsumed(rule) {
        if (!rule.once) return false;
        if (rule.once === "page") return this.pageOnce[rule.id] === true;

        try {
            const raw = window.sessionStorage.getItem(ONCE_STORE_KEY);
            const consumed = raw ? JSON.parse(raw) : {};
            return consumed[rule.id] === true;
        } catch (e) {
            return false;
        }
    }

    consumeOnce(rule) {
        if (!rule.once) return;
        if (rule.once === "page") {
            this.pageOnce[rule.id] = true;
            return;
        }
        try {
            const raw = window.sessionStorage.getItem(ONCE_STORE_KEY);
            const consumed = raw ? JSON.parse(raw) : {};
            consumed[rule.id] = true;
            window.sessionStorage.setItem(ONCE_STORE_KEY, JSON.stringify(consumed));
        } catch (e) {
            /* private mode: bellekteki isaret yeterli */
            this.pageOnce[rule.id] = true;
        }
    }

    buildContext(element, event) {
        return {
            el: element || null,
            event: event || null,
            siteId: this.siteId,
            page: {
                url: String(window.location.href),
                path: window.location.pathname,
                query: window.location.search,
                title: document.title || "",
                referrer: document.referrer || "",
                pattern: pathMatcher.resolvePattern(),
            },
            visitor: this.tracker ? this.tracker.visitorInfo() : {},
        };
    }

    /**
     * Kurali degerlendirir ve event'i uretir.
     * Property cozumlemesi async olabilecegi icin event tum degerler hazir oldugunda gider;
     * zaman damgasi tetiklenme aninda sabitlenir.
     */
    trigger(rule, element, event) {
        const self = this;
        const context = this.buildContext(element, event);
        const triggeredAt = Date.now();
        const jsErrors = [];
        const options = {
            onJsError: function (prop, error) {
                jsErrors.push({ prop: prop, error: error });
            },
        };

        if (this.isOnceConsumed(rule)) return;
        // Tekrar kontrolu cozumleme beklenmeden isaretlenir: async sirasinda ayni kural
        // ikinci kez tetiklenip cift event uretmesin
        this.consumeOnce(rule);

        evaluateCondition(rule, context, options)
            .then(function (allowed) {
                if (!allowed) return null;
                return resolveProps(rule, context, options).then(function (props) {
                    self.dispatch(rule, props, context, triggeredAt, jsErrors);
                });
            })
            .catch(function (e) {
                log.debug("rule trigger failed", { rule: rule.id, message: e.message });
            });
    }

    dispatch(rule, props, context, triggeredAt, jsErrors) {
        if (this.testHandler) {
            // Test modu: event transport'a gitmez, panele geri gonderilir
            this.testHandler({
                ruleId: rule.id,
                name: rule.name,
                props: props,
                page: context.page,
                ts: triggeredAt,
                errors: jsErrors,
            });
            return;
        }

        if (!this.tracker) return;
        this.tracker.emit(rule.name, props, {
            source: RULE_SOURCE,
            ts: triggeredAt,
        });
    }

    /**
     * Editor test modu. Handler verilince event'ler panele yonlendirilir,
     * null verilince normal gonderime donulur.
     */
    setTestHandler(handler) {
        this.testHandler = typeof handler === "function" ? handler : null;
    }

    isTestMode() {
        return !!this.testHandler;
    }
}

export const rulesEngine = new RulesEngine();
export default rulesEngine;



//////////////////
// WEBPACK FOOTER
// ./static/src/tracker/rules/engine.js
// module id = null
// module chunks = 